import axios from "axios";

/**
 * Unwrap a caught error into a user-facing message, preferring the
 * backend's 400 `detail` string over axios's generic
 * "Request failed with status code 400". The `detail` body is where
 * workflow-guard copy lives (e.g. `TRANSITION_GUARDS` messages like "A WC
 * evaluation with outcome 'Recommend' (score ≥ 30) is required before
 * forwarding to the SC") — surfacing it verbatim is the whole point of
 * this helper, since the axios default swallows it.
 *
 * Falls back to a plain `Error`'s `.message`, then to the caller-supplied
 * `fallback` for anything else.
 *
 * Extracted from `EvaluationFormCard.tsx`'s original `evaluationErrorMessage`
 * (Task 12) into a shared helper so `StatusActionPanel` and
 * `VerificationCard` get the same detail-first unwrap instead of each
 * re-deriving it (or, in `StatusActionPanel`'s case, not doing it at all —
 * see the final-review-pass fix that added this shared import there).
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | {
          detail?: string;
          errors?: { field?: string; message?: string }[];
        }
      | undefined;
    // 422 shape: the backend's RequestValidationError handler pins
    // `detail` to the useless "Validation error" and puts the per-field
    // messages in `errors` — prefer those when present.
    const errors = data?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return errors
        .map((e) => (e.field ? `${e.field}: ${e.message}` : e.message))
        .join("; ");
    }
    const detail = data?.detail;
    if (detail && typeof detail === "string") return detail;
    return err.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
}
